import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

/**
 * Standard in-app page width. Centers content at `max-w-4xl` so every chrome
 * page shares one gutter instead of repeating `mx-auto max-w-4xl ...` per file.
 *
 * - `scroll` (default) — content pages: `mx-auto max-w-4xl` + `pb-16` gutter.
 * - `fill`             — list/table pages owning their own scroll: full-height flex
 *                        column at `md+`. On phones it relaxes to natural height so
 *                        the whole page scrolls instead of cramming its table/content
 *                        into a fixed viewport (a short phone otherwise squeezes the
 *                        list to a sliver — see the sandbox/members mobile fixes).
 * - `plain`            — just the centered max-width.
 * - `fluid`            — full width, no `max-w-4xl` (dense operator/table pages).
 *
 * Width is chosen per page to MATCH its current width — this never normalizes
 * a page to a new width. Per-page spacing (`space-y-6`, `gap-4`) via `className`.
 */
export type PageVariant = 'scroll' | 'fill' | 'plain' | 'fluid';

export function PageContainer({
  children,
  variant = 'scroll',
  className,
}: {
  children: ReactNode;
  variant?: PageVariant;
  className?: string;
}) {
  return (
    <div
      className={cn(
        variant === 'fluid' ? 'w-full' : 'mx-auto max-w-4xl',
        variant === 'scroll' && 'pb-16',
        variant === 'fill' && 'flex flex-col md:h-full md:min-h-0',
        className,
      )}
    >
      {children}
    </div>
  );
}
